import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';

import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import {
  AccessServiceInterfaceToken,
  LoggedInUserInfoServiceInterfaceToken,
  LoopV2Module,
  Market as LoopMarket,
  SalesTeamsServiceInterfaceToken,
} from '@vendasta/sales-common';
import { StencilsModule } from '@vendasta/store';
import { VaBreadcrumbsModule, VaCardsModule } from '@vendasta/uikit';
import { HighlightsModule } from '../highlights';

import { DashboardComponent } from './dashboard.component';
import { DashboardService } from './dashboard.service';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LexiconModule } from '@galaxy/lexicon';
import { GalaxyProgressBarModule } from '@vendasta/galaxy/progress-bar';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import baseTranslation from '../../assets/i18n/en_devel.json';
import { AssignedSalespersonModule } from '../assigned-salesperson/assigned-salesperson.module';
import { CreateSnapshotModule } from '../businesses/details/views/overview/components/create-snapshot/create-snapshot.module';
import { SafeUrlPipe } from '../common/pipes/safe-url.pipe';
import { UnifiedTOSDialogComponent } from '../common/tos-unified/unified-tos-dialog.component';
import { VaCarouselComponent } from '../common/va-carousel/va-carousel.component';
import { WEBLATE_COMPONENT_NAME } from '../constants';
import { StubAccessService } from '../core/access';
import { Market } from '../core/markets/market';
import { PartnerService } from '../core/partner.service';
import { LoggedInUserInfoService } from '../sales/team-activity-page/logged-in-user-info.service';
import { SalesTeamsService } from '../sales/team-activity-page/sales-teams.service';
import { SubscriptionTierModule } from '../subscription-tier/subscription-tier.module';
import { AnnouncementBannerComponent } from './announcement-banner/announcement-banner.component';
import { BusinessAccountsComponent } from './business-accounts/business-accounts.component';
import { ContactSalesDialogComponent } from './cta-dialog/cta-dialog.component';
import { DashboardRoutingModule } from './dashboard-routing.module';
import { HomePageCardsModule } from './home-page-cards/home-page-cards.module';
import { MessageBoxComponent } from './message-box/message-box.component';
import { SnapshotReportComponent } from './snapshot-report/snapshot-report.component';
import { WelcomeModalComponent } from './welcome-modal/welcome-modal.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    RouterModule,
    DashboardRoutingModule,
    MatTooltipModule,
    MatButtonModule,
    MatStepperModule,
    MatTabsModule,
    MatCardModule,
    MatIconModule,
    MatDialogModule,
    HighlightsModule,
    StencilsModule,
    MatSelectModule,
    MatDividerModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    VaCardsModule,
    TranslateModule,
    LexiconModule.forChild({
      componentName: WEBLATE_COMPONENT_NAME,
      baseTranslation: baseTranslation,
    }),
    HomePageCardsModule,
    CreateSnapshotModule,
    LoopV2Module,
    SubscriptionTierModule,
    AssignedSalespersonModule,
    GalaxyAlertModule,
    GalaxyPageModule,
    GalaxyTooltipModule,
    VaBreadcrumbsModule,
    ProductAnalyticsModule,
    SafeUrlPipe,
    UnifiedTOSDialogComponent,
    VaCarouselComponent,
    GalaxyProgressBarModule,
  ],
  providers: [
    DashboardService,
    PartnerService,
    { provide: SalesTeamsServiceInterfaceToken, useClass: SalesTeamsService },
    {
      provide: LoggedInUserInfoServiceInterfaceToken,
      useClass: LoggedInUserInfoService,
    },
    { provide: AccessServiceInterfaceToken, useClass: StubAccessService },
    {
      provide: 'partnerId$',
      useExisting: 'PARTNER_ID',
    },
    {
      provide: 'markets$',
      useFactory: (userAccessibleMarkets$: Observable<Market[]>) =>
        userAccessibleMarkets$.pipe(
          map((markets) => {
            return markets.map((m) => {
              return { name: m.name, marketId: m.market_id } as LoopMarket;
            });
          }),
        ),
      deps: ['USER_ACCESSIBLE_MARKETS'],
    },
  ],
  declarations: [
    DashboardComponent,
    MessageBoxComponent,
    ContactSalesDialogComponent,
    BusinessAccountsComponent,
    SnapshotReportComponent,
    AnnouncementBannerComponent,
    WelcomeModalComponent,
  ],
  exports: [DashboardComponent],
})
export class DashboardModule {}
